// PageDescription: 지역별 비급여 가격비교 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React, { useMemo } from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import { TREATMENT_CATEGORIES_IDS } from '@/constants/category';
import {
  NonBenefitHomeQueries,
  TREATMENT_CATEGORIES,
  useNonBenefitMedicinesForSeo,
  useNonBenefitPriceTotalSummary,
  usePharmacyMedicinePriceTotalSummary,
  useSearchDoctorsForSeoByUser,
} from '@/entities/non-benefit';
import useNearbyPharmacyV3ByRegionCode from '@/entities/non-benefit/hooks/useNearbyPharmacyV3ByRegionCode';
import { KoreaAddress, Region } from '@/entities/region';
import useAppInstallTopBanner from '@/hooks/Cuj/useAppInstallTopBanner';
import useGetBannerAdExchange from '@/hooks/ad/useGetBannerAdExchange';
import useSearchKoreaAddressV2 from '@/hooks/hospital/search/useSearchKoreaAddressV2';
import useGetKeywordSearchMagazineInfinite from '@/hooks/search/magazine/useGetKeywordSearchMagazineInfinite';
import useGetKeywordQuestionCardInfinite from '@/hooks/search/qna/useGetKeywordQuestionCardInfinite';
import { AdPageType } from '@/models/ad/list';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { EDiagnosisContactType } from '@/types/diagnosis';
import { nonBenefitSeoLdJson } from '@/utils/format';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import { addQueryString } from '@/utils/url';
import {
  NonBenefitHomeView,
  getAdditionalMetaTags,
  getDescriptionForMetaTag,
  useNonBenefitHomeViewModel,
} from '@/views/non-benefit';
import { useRouter } from 'next/router';

const NonBenefitHomePages = ({ client }: CustomPageProps) => {
  const viewModel = useNonBenefitHomeViewModel(client);
  const topBannerViewModel = useAppInstallTopBanner(client);
  const router = useRouter();
  const { treatmentId } = viewModel.queries as NonBenefitHomeQueries;

  const title = `${viewModel.selectedAddressTitle} ${viewModel.selectedMedicine?.medicineDescription} 처방 최저가 병원/약국 가격(${new Date().getFullYear()}) - 닥터나우`;
  const description = getDescriptionForMetaTag(treatmentId);
  const canonical = `${process.env.NEXT_PUBLIC_DOMAIN}${router.asPath}`;

  const nonBenefitLdJson = useMemo(() => {
    return nonBenefitSeoLdJson({
      doctors: viewModel.nonBenefitDoctors,
      address: viewModel.selectedAddressTitle,
      faqs: viewModel.faqs,
      canonical,
    });
  }, [viewModel.selectedMedicine]);

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonical}
        openGraph={{
          title,
          description: description,
          url: canonical,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_MAIN_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
        additionalMetaTags={getAdditionalMetaTags(treatmentId)}
      />
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(nonBenefitLdJson) }} />
      </Head>
      <NonBenefitHomeView client={client} viewModel={viewModel} topBannerViewModel={topBannerViewModel} />
    </>
  );
};

NonBenefitHomePages.Layout = DefaultLayout;
export default NonBenefitHomePages;

const SEOUL_REGION_CODE = '11';

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (ctx) => {
  const queryClient = new QueryClient();
  const queries = ctx.query as NonBenefitHomeQueries;

  /* 쿼리가 없는 경우 기본 데이터로 리다이렉트 */
  if (!queries.treatmentId || !queries.regionCode) {
    return {
      redirect: {
        permanent: false,
        destination:
          ROUTES.NON_BENEFIT.HOME.path +
          addQueryString({
            treatmentId: queries.treatmentId ?? TREATMENT_CATEGORIES_IDS.Hairloss,
            regionCode: queries.regionCode ?? SEOUL_REGION_CODE,
          }),
      },
    };
  }

  const contactType =
    TREATMENT_CATEGORIES.find((category) => category.id === queries.treatmentId)?.contactType ??
    EDiagnosisContactType.NonF2F;

  const nonBenefitMedicines = await useNonBenefitMedicinesForSeo.fetchQuery(queryClient, {
    treatmentId: queries.treatmentId,
    contactType,
  });

  // treatmentMedicineUnitId로부터 해당하는 medicine 찾기
  const selectedMedicine = nonBenefitMedicines.findMedicineByTreatmentMedicineUnitId(queries.treatmentMedicineUnitId);

  // selectedMedicine이 없으면 (잘못된 treatmentMedicineUnitId) 첫 번째 medicine 사용
  const treatmentMedicineId = selectedMedicine?.id || nonBenefitMedicines.medicines[0]?.id;

  /* 색인 이슈로 잘못된 treatmentMedicineUnitId가 유입되고 있어 매칭되는 medicine이 없으면 리다이렉트 */
  const medicine = nonBenefitMedicines.findMedicineByQueryTreatmentMedicineUnitId(queries.treatmentMedicineUnitId);

  if (!medicine && nonBenefitMedicines.medicines.length > 0) {
    const defaultMedicine = nonBenefitMedicines.medicines[0];
    const treatmentMedicineUnitId = defaultMedicine.units[0]?.id;

    if (treatmentMedicineUnitId) {
      return {
        redirect: {
          permanent: false,
          destination:
            ROUTES.NON_BENEFIT.HOME.path +
            addQueryString({
              treatmentId: queries.treatmentId,
              treatmentMedicineUnitId: treatmentMedicineUnitId,
              regionCode: queries.regionCode,
            }),
        },
      };
    }
  }

  const queryRegion = new Region(queries.regionCode);

  const siDoList = await useSearchKoreaAddressV2.fetcher({
    siDoCode: null,
    siGunGuCode: null,
  });
  const siGunGuList = await useSearchKoreaAddressV2.fetcher({
    siDoCode: queryRegion.excludeSejongSiDoRegionCode(),
    siGunGuCode: null,
  });
  const dongList = await useSearchKoreaAddressV2.fetcher({
    siDoCode: queryRegion.regionCodes.siDoCode,
    siGunGuCode: queryRegion.excludeSejongSiGunGuRegionCode(),
  });

  /* 선택된 지역 */
  const koreaAddress = new KoreaAddress(siDoList, siGunGuList, dongList);

  await Promise.allSettled([
    useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.TopCtaBand, ctx.client),
    useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.NonBenefitDetail, ctx.client),
    useSearchKoreaAddressV2.prefetchQuery(queryClient, {
      siDoCode: null,
      siGunGuCode: null,
    }),
    useSearchKoreaAddressV2.prefetchQuery(queryClient, {
      siDoCode: queryRegion.excludeSejongSiDoRegionCode(),
      siGunGuCode: null,
    }),
    useSearchKoreaAddressV2.prefetchQuery(queryClient, {
      siDoCode: queryRegion.regionCodes.siDoCode,
      siGunGuCode: queryRegion.regionCodes.siGunGuCode,
    }),
    useNonBenefitPriceTotalSummary.prefetchQuery(queryClient, treatmentMedicineId, queries.regionCode, contactType),
    usePharmacyMedicinePriceTotalSummary.prefetchQuery(
      queryClient,
      treatmentMedicineId,
      queries.regionCode,
      contactType
    ),
    useSearchDoctorsForSeoByUser.prefetchQuery(
      queryClient,
      useSearchDoctorsForSeoByUser.getNonBenefitHomeParams(queries.regionCode, queries.treatmentMedicineUnitId ?? ''),
      contactType
    ),
    useGetKeywordQuestionCardInfinite.prefetchQuery(
      queryClient,
      useGetKeywordQuestionCardInfinite.getNonBenefitHomeQnaParams(selectedMedicine?.medicineDescription ?? '')
    ),
    useGetKeywordSearchMagazineInfinite.prefetchArticle(
      queryClient,
      useGetKeywordSearchMagazineInfinite.getNonBenefitHomeArticleParams(selectedMedicine?.medicineDescription ?? '')
    ),
    useNearbyPharmacyV3ByRegionCode.prefetchQuery(queryClient, {
      regionCode: queries.regionCode,
      userLocation: koreaAddress.getSelectedLocation(queryRegion),
      treatmentId: selectedMedicine.treatment?.id ?? '',
      treatmentMedicineId: selectedMedicine.id,
      dispensationUnit:
        nonBenefitMedicines
          ?.findDispensationUnitByTreatmentMedicineUnitId(
            queries.treatmentMedicineUnitId,
            nonBenefitMedicines.findMedicineUnitByTreatmentMedicineUnitId(queries.treatmentMedicineUnitId)
          )
          ?.getDispensationUnit() ?? 0,
    }),
  ]);

  return {
    props: {
      queries,
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
});
